import { useEffect, useRef, useState } from 'react';

import { Link, useParams } from 'react-router-dom';

import { useApolloClient } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { InternalUseOnlyLabel } from '@movable/ui';
import {
  Box,
  ClickAwayListener,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import usePrimaryNavStore from '@/components/shared/PrimaryNavDrawer/PrimaryNavStore';
import SwitchAccountModal from '@/components/shared/PrimaryNavDrawer/SwitchAccountModal';
import {
  LEGACY_DOMAINS,
  NavItem,
  accessFilter,
  getUrl,
} from '@/components/shared/PrimaryNavDrawer/utils';
import { UserFeatureAccessType } from '@/generated';
import { useSession } from '@/providers/SessionProvider/SessionProvider';
import usePermissionAccess from '@/providers/UserPermissionsProvider/hooks/usePermissionAccess';
// TODO: feature_flag_cleanup: hasAccountPermissions - Remove this import when FF is fully rolled out
import useRoleAccess from '@/providers/UserPermissionsProvider/hooks/useRoleAccess';
import useRecoilDebuggerStore from '@/store/RecoilDebugger/useRecoilDebuggerStore';
import { DeepReadonly } from '@/utils/globalTypes';

import PrimaryNavClientCardWrapper from './PrimaryNavClientCard/PrimaryNavClientCardWrapper';

const PrimaryNavClientActions = () => {
  const { clientId = '' } = useParams<{ clientId: string }>();
  const { logout } = useAuth0();
  const apolloClient = useApolloClient();
  const theme = useTheme();

  const {
    clients,
    currentUser,
    currentClient,
    currentUserAccounts,
    organizationId,
    isSuperuser,
    isOrganizationOwner,
    isOrganizationAdmin,
  } = useSession();
  const {
    isClientActionsSubMenuOpen,
    setIsDrawerOpen,
    setIsClientActionsSubMenuOpen,
    setSwitchAccountModalIsOpen,
  } = usePrimaryNavStore();

  const clientActionsRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const { isRecoilDrawerOpen, setIsReoilDrawerOpen } = useRecoilDebuggerStore();

  const currentAccount = currentUserAccounts.get(currentClient?.name ?? '');

  const { hasPermission } = usePermissionAccess();
  // TODO: feature_flag_cleanup: hasAccountPermissions - Remove useRoleAccess when FF is fully rolled out
  const { hasRole } = useRoleAccess();

  const hasAccountPermissions = currentClient?.hasAccountPermissions ?? false;

  const SUB_MENU_ITEMS: DeepReadonly<NavItem[]> = [
    {
      id: 'switch-account',
      title: (
        <Stack direction="row" spacing={0.5}>
          <Typography>Switch Account</Typography>
          <Typography color="neutral600">(Option/Alt + S)</Typography>
        </Stack>
      ),
      route: '',
      domains: null,
      isExternal: false,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
    },
    {
      id: 'account-admin',
      title: 'Account Management',
      route: `/organizations/${organizationId}/accounts/${currentAccount?.id}/users`,
      domains: null,
      isExternal: false,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
    },
    {
      id: 'organization',
      title: 'Organization Administration',
      route: isSuperuser
        ? '/organizations'
        : `/organizations/${organizationId}/users`,
      domains: null,
      isExternal: false,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      visibilityCriteria:
        isSuperuser || isOrganizationOwner || isOrganizationAdmin,
    },
    {
      id: 'help-center',
      title: 'Help Center',
      route: '/',
      domains: {
        staging: 'https://davinci-mi.zendesk.com/hc/en-us',
        prod: 'https://davinci-mi.zendesk.com/hc/en-us',
        local: 'https://davinci-mi.zendesk.com/hc/en-us',
      },
      isExternal: true,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      visibilityCriteria: false,
    },
    {
      id: 'logout',
      title: 'Logout',
      route: '',
      domains: null,
      isExternal: false,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
    },
  ];

  const INTERNAL_USE_ONLY_ITEMS: DeepReadonly<NavItem[]> = [
    {
      id: 'admin',
      title: 'Admin',
      route: '/content_library_admin/',
      domains: LEGACY_DOMAINS,
      isExternal: true,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: true,
    },
    {
      id: 'admin-task-list',
      title: 'Admin Task List',
      route: '/admin_tasks/',
      domains: LEGACY_DOMAINS,
      isExternal: true,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: true,
      shouldSwitchClients: true,
    },
    {
      id: 'graphiql',
      title: 'GraphiQL',
      domains: null,
      route: '/graphql',
      featureAccess: UserFeatureAccessType.Experimental,
      isStaff: true,
    },
    {
      id: 'recoil-debugger',
      title: 'Recoil Debugger',
      domains: null,
      featureAccess: UserFeatureAccessType.Experimental,
      isStaff: true,
      action: () => {
        setIsReoilDrawerOpen(!isRecoilDrawerOpen);
        setIsClientActionsSubMenuOpen(false);
      },
    },
  ];

  const canSwitchClients = clients.length > 1;

  const handleActionItemClick = (item: NavItem) => {
    if (item.id === 'switch-account') {
      setSwitchAccountModalIsOpen(true);
      setIsClientActionsSubMenuOpen(false);
      return;
    }

    if (item.id === 'logout') {
      if (import.meta.env.VITE_E2E) {
        localStorage.removeItem('auth0AccessToken');
      }

      window.mixpanel?.reset();
      logout();
      apolloClient.clearStore();
      return;
    }

    if (item.action) {
      item.action();
    }
  };

  const getItemUrl = (item: NavItem) => {
    if (item.id === 'switch-account' || item.id === 'logout' || item.action) {
      return undefined;
    }

    return getUrl(
      clientId,
      item?.route ?? '',
      item.domains,
      item.isExternal ?? false,
      item.shouldSwitchClients,
    );
  };

  const handleLinkClick = () => {
    setIsDrawerOpen(false);
    setIsClientActionsSubMenuOpen(false);
  };

  const renderMenuItem = (item: NavItem) => {
    const url = getItemUrl(item);
    const isAction = !url;
    const isExternal = item.isExternal;

    return (
      <MenuItem
        key={item.id}
        component={isAction ? 'li' : Link}
        to={!isAction ? url : undefined}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        onClick={isAction ? () => handleActionItemClick(item) : handleLinkClick}
        sx={{
          '&:hover': {
            backgroundColor: theme.primaryNav.hoverBackgroundColor,
          },
        }}
      >
        <ListItemText>{item.title}</ListItemText>
      </MenuItem>
    );
  };

  const getVisibleSubMenuItems = () => {
    // The 2nd filter is to remove the Switch Account option if there is only one client
    return SUB_MENU_ITEMS.filter((item) =>
      accessFilter({
        currentClient,
        currentUser,
        navItem: item,
        hasAccountPermissions,
        hasPermission,
        hasRole,
      }),
    ).filter((item) => item.id !== 'switch-account' || canSwitchClients);
  };
  const visibleSubMenuItems = getVisibleSubMenuItems();

  const visibleInternalUseOnlyItems = INTERNAL_USE_ONLY_ITEMS.filter((item) =>
    accessFilter({
      currentClient,
      currentUser,
      navItem: item,
      hasAccountPermissions,
      hasPermission,
      hasRole,
    }),
  );

  const handleSubMenuClickAway = () => {
    setIsClientActionsSubMenuOpen(false);
  };

  useEffect(() => {
    if (clientActionsRef.current) {
      const element = clientActionsRef.current;
      if (element) {
        setAnchorEl(element);
      }
    }
  }, [clientActionsRef]);

  return (
    <>
      <PrimaryNavClientCardWrapper ref={clientActionsRef} />
      <Popover
        open={isClientActionsSubMenuOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ ml: 1 }}
      >
        <ClickAwayListener onClickAway={handleSubMenuClickAway}>
          <MenuList
            sx={{
              color: 'white',
              letterSpacing: '0.15px',
              backgroundColor: theme.primaryNav.backgroundColor,
              borderRadius: 1,
            }}
          >
            {visibleSubMenuItems.map((item) => renderMenuItem(item))}
            {visibleInternalUseOnlyItems.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <InternalUseOnlyLabel sx={{ px: 2, py: 1 }} />
                {visibleInternalUseOnlyItems.map((item) =>
                  renderMenuItem(item),
                )}
              </Box>
            )}
          </MenuList>
        </ClickAwayListener>
      </Popover>
      <SwitchAccountModal />
    </>
  );
};

export default PrimaryNavClientActions;
